import * as React from 'react';
import { FunctionComponent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getUser, getClient, logoutThunk } from 'features/app/redux/context';
import { translate } from '../../../../utils/locale';

import { UserProfileDropdown, SecondaryNavLink } from 'basecamp';

// TODO - import from connect utils in prod

import { endMATViewAs } from '../../../../utils/impersonation';
import * as impersonationUtils from '../../../../utils/impersonation';

/**
 ** Props
 */
type Props = {
	close: () => void;
};

const UserProfileMenu: FunctionComponent<Props> = (props) => {
	const dispatch = useDispatch();

	/**
	 ** Handle Selectors
	 */
	// Get user information
	const user = useSelector(getUser);
	// Get client information
	const client = useSelector(getClient);

	/**
	 ** Get client impersonation type
	 */
	const isImpostor = impersonationUtils.isImpostor(user.impersonationMode);

	/**
	 ** Render Component
	 */
	return (
		<UserProfileDropdown
			school={client.name}
			username={user.fullname}
			email={user.username}
			andImpersonate={user.impersonationMode}
			impersonationUtils={impersonationUtils}
			close={() => props.close()}
			boxShadow="lg"
			dataTest="userProfileDropdown"
			zIndex={9999}
			top={70}
		>
			{/* Handle impersonation button */}
			<SecondaryNavLink
				setAs={isImpostor ? 'primary' : 'primary'}
				withInternalUrl={
					user?.impersonationMode === 'MAT'
						? '/home'
						: isImpostor
						? '/auth/login'
						: '/select-organisation'
				}
				onClick={(e: React.MouseEvent<HTMLElement>) => {
					props.close();
					if (user?.impersonationMode === 'MAT') {
						// endMATViewAs() owns closing this impersonation tab (fallback: /home).
						// Prevent SecondaryNavLink from ALSO pushing '/auth/login' (the race). CI 52102.
						e.preventDefault();
						endMATViewAs();
					}
					if (user?.impersonationMode === 'Full' || user?.impersonationMode === 'Client') {
						localStorage.removeItem('keyStageStorage');
						dispatch(logoutThunk());
					}
				}}
				withIcon={isImpostor ? 'user-secret' : 'school'}
				dataTest="impersonationBtn"
			>
				{isImpostor
					? translate('standardPage.userProfileDropdown.END_IMPERSONATION')
					: translate('standardPage.userProfileDropdown.SELECT_ORGANISATION')}
			</SecondaryNavLink>

			{/* Sign out button */}
			<SecondaryNavLink
				setAs={isImpostor ? 'primary' : 'primary'}
				withIcon="right-from-bracket"
				dataTest="signOutBtn"
				onClick={(e: React.MouseEvent<HTMLElement>) => {
					e.preventDefault();
					// From a MAT "view as school" tab, signing out must NOT sign out the MAT.
					// Auth is a shared cookie, so we deliberately skip the server logout and the
					// cross-tab broadcast — we just clear this tab's impersonation and close it,
					// leaving the Summit (MAT) tab (and any other viewed-as school tabs) signed
					// in. Same single-tab outcome as ending impersonation. See CI 52102 point 2.
					if (user?.impersonationMode === 'MAT') {
						endMATViewAs();
					} else {
						dispatch(logoutThunk());
					}
				}}
			>
				{translate('standardPage.userProfileDropdown.SIGN_OUT')}
			</SecondaryNavLink>
		</UserProfileDropdown>
	);
};

export default UserProfileMenu;
